import type { SxProps, Theme } from '@mui/material';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import { type ComponentType, forwardRef, useMemo } from 'react';
import {
  type FullCombinator,
  type FullField,
  type FullOperator,
  QueryBuilder,
  type QueryBuilderProps,
  type RuleGroupType,
  type ValueEditorProps,
} from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import {
  CONDITION_CONTROLS,
  createAddGroupAction,
  withConditionValueCell,
} from './condition-control-elements';
import { getConditionGroupLevel } from './condition-fields';
import { ConditionsBuilderRoot } from './conditions-builder.styles';

type BuilderQueryProps = QueryBuilderProps<
  RuleGroupType,
  FullField,
  FullOperator,
  FullCombinator
>;

export type ConditionsBuilderProps = Omit<
  BuilderQueryProps,
  | 'controlElements'
  | 'showCombinatorsBetweenRules'
  | 'enableDragAndDrop'
  | 'onAddGroup'
  | 'onMoveGroup'
> & {
  /** The screen's own value editor; it is placed in the row's value column. */
  valueEditor: ComponentType<ValueEditorProps>;
  /** How deep groups may nest; leaves out `+ Group` and refuses a drop past it. */
  maxGroupLevel?: number | null;
  enableDragAndDrop?: boolean;
  'data-testid'?: string;
  sx?: SxProps<Theme>;
};

/**
 * One conditions builder for every screen that filters by conditions: the same fields, row
 * layout, AND / OR column, group boxes and add / remove controls, on a page, in a dialog or on
 * a phone. Screens pass their own fields, operators, value editor and context.
 */
export const ConditionsBuilder = forwardRef<HTMLDivElement, ConditionsBuilderProps>(
  (
    {
      valueEditor,
      maxGroupLevel,
      enableDragAndDrop = false,
      'data-testid': testId,
      sx,
      ...queryBuilderProps
    },
    ref,
  ) => {
    const valueCell = useMemo(() => withConditionValueCell(valueEditor), [valueEditor]);
    const controlElements = useMemo(
      () => ({
        ...CONDITION_CONTROLS,
        addGroupAction: createAddGroupAction(maxGroupLevel),
        valueEditor: valueCell,
      }),
      [maxGroupLevel, valueCell],
    );

    const groupLimit = maxGroupLevel ?? null;
    const groupLevel = queryBuilderProps.query
      ? getConditionGroupLevel(queryBuilderProps.query)
      : 0;

    return (
      <ConditionsBuilderRoot ref={ref} data-testid={testId} sx={sx} groupLevel={groupLevel}>
        <QueryBuilderDnD enableDragAndDrop={enableDragAndDrop}>
          <QueryBuilder
            {...queryBuilderProps}
            enableDragAndDrop={enableDragAndDrop}
            showCombinatorsBetweenRules
            controlElements={controlElements}
            onAddGroup={(group, parentPath) =>
              groupLimit === null || parentPath.length < groupLimit ? group : false
            }
            // eslint-disable-next-line max-params -- react-querybuilder passes five positional arguments
            onMoveGroup={(_group, _fromPath, _toPath, _query, nextQuery) =>
              groupLimit === null || getConditionGroupLevel(nextQuery) <= groupLimit
            }
          />
        </QueryBuilderDnD>
      </ConditionsBuilderRoot>
    );
  },
);

ConditionsBuilder.displayName = 'ConditionsBuilder';
